import React, { createContext, useContext, useState } from "react";
import axios from "axios";
import {useUI} from "../ui/uiContext";
import Transform from "classes/transform";
import { format } from "date-fns";
import useLocalStorage from "hooks/useLocalStorage";
import { useNavigate } from "react-router-dom";
/**
 * DealerContext is a React context that provides dealer-specific data and functionalities.
 * It allows components to consume and act on dealer-related information such as inspection data.
 */
const DealerContext = createContext();

/**
 * Custom hook to access the DealerContext.
 * @returns {object} The DealerContext with inspection data and related functionalities.
 */
export const useDealer = () => useContext(DealerContext);

/**
 * DealerProvider is a context provider component that manages dealer-specific state,
 * including inspection data and API interactions.
 * @param {object} props - Props for the DealerProvider component.
 * @param {React.ReactNode} props.children - Child components that will have access to DealerContext.
 * @returns {React.ReactElement} A context provider component wrapping its children.
 */
export const DealerProvider = ({ children }) => {
  const [dealerVehicles, setDealerVehicles] = useLocalStorage("dealerVehicles", "[]");
  const [inspection, setInspection] = useLocalStorage("inspection", "");
  const [inspectionID, setInspectionID] = useLocalStorage("inspectionID", "");
  const [charges, setCharges] = useLocalStorage("charges", "");
  const {showAlert, showLoadingSpinner, hideLoadingSpinner} = useUI();
  const [inspectionData, setInspectionData] = useLocalStorage("inspectionData", "");  //useState(null);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useLocalStorage(
    "startDate",
    new Date(new Date().setDate(new Date().getDate() - 7)),
    (value) => (value ? new Date(value) : new Date())
  );

  const [endDate, setEndDate] = useLocalStorage(
    "endDate",
    new Date(),
    (value) => (value ? new Date(value) : new Date())
  );
  const [selectedRange, setSelectedRange] = useLocalStorage("selectedRange", 7);

  const navigate = useNavigate();

  const [images, setImages] = useState([]);

  // Create an Axios instance with the interceptor
  const axiosInstance = axios.create({
    baseURL: window?.globalConfig.rvlApiUrl
  });

  axiosInstance.interceptors.request.use((config) => {
    const token = localStorage.getItem("sessionToken_dealer");
    if (token) {
      config.data = { ...config.data, sessionToken: token };
    }
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => {
      // Check if the response contains a message with "unauthorized"
      if (response.data.message && response.data.message.toLowerCase().includes("unauthorized")) {
        navigate("/dealer/login"); // Redirect to the login page
      }
      return response; // Return the response if no navigation occurs
    },
    (error) => {
      // Handle other errors
      if (error.response && error.response.data && error.response.data.message) {
        if (error.response.data.message.toLowerCase().includes("unauthorized")) {
          navigate("/dealer/login"); // Redirect on error response as well
        }
      }
      return Promise.reject(error); // Reject the promise if error occurs
    }
  );

  /**
   * Fetches inspection data for a specific vehicle model.
   * Updates the inspectionData state and handles any API errors.
   * @param {string} vehicleModelID - The ID of the vehicle model to fetch inspection data for.
   * @async
   */
  const fetchInspectionData = async (vehicleModelID) => {
    try {
      const response = await axiosInstance.post("/api/GetInspectionMaster", { vehicleModelID });
      if (response.data.success) {
        setInspectionData(response.data.inspectionOptions);
        console.log(response.data.inspectionOptions);
        setError(null);
      } else {
        setError(response.data.message || "Failed to fetch inspection data");
      }
    } catch (error) {
      console.error("Error fetching inspection data", error);
      setError(error.message || "An error occurred during the fetch");
    }
  };

  /**
   * Asynchronously fetches dealer vehicles from the API based on the provided date range.
   * This function sends a POST request to the "/api/Dealer/Vehicles" endpoint with a payload containing
   * the start and end dates. On successful fetch, it updates the dealerVehicles state with the response data,
   * and on failure, it sets an error state with the relevant error message.
   * @param {string} start - The start date for filtering the vehicle data.
   * @param {string} end - The end date for filtering the vehicle data.
   * @returns {Promise<void>} A promise that resolves when the data is fetched and the state is updated.
   * @async
   * @example
   * await fetchDealerVehicles('2022-01-01', '2022-12-31');
   */
  const fetchDealerVehicles = async (
    start = format(startDate, "yyyy-MM-dd"),
    end = format(endDate, "yyyy-MM-dd")
  ) => {
    const payload = {
      startDate: start,
      endDate: end,
    };

    try {
      const response = await axiosInstance.post("/api/Dealer/Vehicles", payload);

      if (response.data.success) {
        console.log(response.data);
        setDealerVehicles(response?.data?.vehicles);
        setError(null);
      } else {
        setError(response.data.message || "Failed to fetch inspection data");
      }
    } catch (error) {
      console.error("Error fetching inspection data", error);
      setError(error.message || "An error occurred during the fetch");
    }
  };

  /**
   * Filters vehicles based on the provided VIN (Vehicle Identification Number).
   * Sends a POST request to the `/api/SearchVehicleVIN` endpoint with the VIN to retrieve matching vehicles.
   * @async
   * @function filterVehicles
   * @param {object} params - Parameters for filtering vehicles.
   * @param {string} params.vin - The VIN of the vehicle to search for.
   * @returns {Promise<void>} - A promise that resolves when the operation is complete.
   * @throws Will log an error and set an error message if the request fails.
   * @example
   * filterVehicles({ vin: '1HGCM82633A123456' });
   */
  const filterVehicles = async ({vin}) => {
    try {
      const response = await axiosInstance.post("/api/SearchVehicleVIN", {vin});

      if (response.data.success) {
        console.log(response.data);
        setDealerVehicles(response?.data?.vehicles);
        setError(null);
      } else {
        setError(response.data.message || "Failed to fetch inspection data");
      }
    } catch (error) {
      console.error("Error fetching inspection data", error);
      setError(error.message || "An error occurred during the fetch");
    }

  };

  /**
   * Asynchronously fetches details of a specific vehicle by its ID from the API.
   * This function sends a POST request to the "/api/Dealer/VehicleDetails" endpoint with the vehicle ID.
   * On successful fetch, it returns the vehicle details object, and on failure, it throws an error.
   * @param {object} params - The parameters object.
   * @param {string} params.vehicleID - The unique identifier of the vehicle.
   * @returns {Promise<object>} A promise that resolves to the vehicle details object.
   * @throws Will throw an error if the API call fails.
   * @async
   * @example
   * const vehicleDetails = await fetchVehicleDetails({ vehicleID: "898de745-9098-444c-9072-26bb4570966e" });
   */
  async function fetchVehicleDetails({ vehicleID }) {
    try {
      const response = await axiosInstance.post("/api/Dealer/VehicleDetails", { vehicleID });

      const details = dealerVehicles.filter(vehicle => vehicle.vehicleId === vehicleID);
      if (response.data.success) {
        return {
          ...response.data.vehicle,
          ...details[0]
        };
      } else {
        throw new Error(response.data.message || "Failed to fetch vehicle details");
      }
    } catch (error) {
      console.error("Error fetching vehicle details", error);
      throw error;
    }
  }


  const fetchCRMaster = async ({ vehicleID }) => {
    try {
      /** Get model ID */
      if (!dealerVehicles || dealerVehicles.length < 1) {
        await fetchDealerVehicles();
      }

      console.log("dealerVehicles", dealerVehicles);

      const vehicle = dealerVehicles.filter((item) => item.vehicleId === vehicleID);

      const {vehicleModelID} = vehicle[0];

      const response = await axiosInstance.post("/api/GetInspectionMaster", {vehicleModelID});
      console.log(response.data);
      setInspection(response.data);
    }

    catch (error) {
      console.log(error);
      showAlert(error.message, "error");
    }
  };

  /**
   * Submits the power of attorney (POA) details for a specific vehicle.
   *
   * This function sends a POST request to the server to confirm the POA details for a vehicle.
   * It accepts a `vehicleID` and a `poa` object containing relevant information and sends this data
   * to the `/api/Dealer/POAConfirmation` endpoint.
   * @async
   * @function submitPOADetails
   * @param {string} vehicleID - The unique identifier for the vehicle.
   * @param {object} poa - An object containing the POA details to be submitted.
   * @param {string} poa.poaSigner1 - The name of the first person authorized.
   * @param {string} poa.poaSigner1Email - The email of the first person authorized.
   * @param {string} [poa.poaSigner2] - The name of the second person authorized (if any).
   * @param {string} [poa.poaSigner2Email] - The email of the second person authorized (if any).
   * @returns {Promise<void>} Resolves when the request is successful.
   * @throws {Error} Logs an error to the console if the request fails.
   */
  const submitPOADetails = async (vehicleID, poa) => {
    try {
      await axiosInstance.post("/api/POAConfirmation", {
        "vehicleID": vehicleID,
        "poaAccepted": true,
        ...poa
      });
    }
    catch (error) {
      console.log(error);
    }
  };

  /**
   * Submits a dealer condition report to the server.
   * @async
   * @function submitDealerConditionReport
   * @param {object} params - The parameters for the report submission.
   * @param {object} params.conditionResult - The result of the condition inspection.
   * @param {number} params.mileage - The mileage of the vehicle.
   * @param {string} params.vehicleID - The ID of the vehicle.
   * @returns {Promise<object>} The result of the report submission.
   * @throws Will throw an error if the submission fails.
   */
  const submitDealerConditionReport = async ({conditionResult, mileage, vehicleID}) => {
    try {

      if (!mileage) {
        showAlert("Mileage is required", "error");
      }

      const vehicle = dealerVehicles.filter((item) => item.vehicleId === vehicleID);

      const {vehicleModelID} = vehicle[0];

      const transform = Transform.transformToAPIFormat(conditionResult);

      const transformedImages = Object.entries(images).map(([name, { url }]) => ({
        name,
        url,
      }));

      const payload = {
        "vehicleID": vehicleID,
        "vehicleModelID": vehicleModelID,
        "milesHours": mileage,
        "inspectionPictures": transformedImages,
        ...transform,
      };

      const response = await axiosInstance.post("/api/Inspection/Create", payload);
      const {success, inspectionID, message} = response.data;

      setInspectionID(inspectionID);

      if (success) {
        setImages([]);
        /** Fetch the total charges */
        const total_charges = await axiosInstance.post("/api/Inspection/GetInspectionItemCharges", {vehicleID, inspectionID});
        const {pickupCharge, selectedItemCharges, totalSelectedItemCharge, dispositionCharge, estimatedMileageCharge, totalActualChargeIncludingPickup  } = total_charges.data;

        setCharges({
          pickupCharge,
          selectedItemCharges,
          totalSelectedItemCharge,
          estimatedMileageCharge,
          totalActualChargeIncludingPickup,
          dispositionCharge,
        })
        return {
          success,
          inspectionID,
          message,
        };
      }

      showAlert(message, "error");
      return {
        success,
        message
      };

    }
    catch (error) {
      console.log(error);
      showAlert(error.message, "error");
      return {
        success: false,
        message: error.message,
      };
    }

  };

  /**
   * Submits a dealer's decision on an offer for a vehicle.
   *
   * This function sends a POST request to the server to submit a dealer's decision on whether to accept or decline an offer for a specific vehicle.
   * If the decision is successfully processed, a success alert is shown; otherwise, an error alert is displayed.
   * @async
   * @function submitDealerOfferDecision
   * @param {object} params - The parameters for the function.
   * @param {string} params.vehicleID - The ID of the vehicle for which the offer decision is being made.
   * @param {boolean} params.offerAccepted - The decision to accept (`true`) or decline (`false`) the offer.
   * @param {string} [params.comment] - An optional comment regarding the offer decision.
   * @returns {Promise<void>} Returns nothing. It displays a success or error alert based on the server response.
   */
  const submitDealerOfferDecision = async ({vehicleID, offerAccepted, comment = ""}) => {
    try {
      const response = await axiosInstance.post("/api/Dealer/DealerOfferDecision", {
        vehicleID,
        offerAccepted,
        comment
      });

      const {success, message} = response.data;

      if (success) {
        await fetchDealerVehicles();
        if (offerAccepted) {
          showAlert("You successfully accepted this offer", "success");
          return;
        }
        showAlert("This offer was declined", "error");
        return;
      }

      showAlert(message, "error");
      console.log(response);
    }
    catch (error) {
      console.log(error);
      showAlert(error.message, "error");
    }
  };

  /**
   * Adds a new image to the existing list of images.
   * @param {object} image - The image object to be added.
   * @param {string} image.name - The name of the image.
   * @param {string} image.pictureID - The unique identifier for the image.
   * @param {string} image.url - The URL of the image.
   * @returns {void}
   */
  const addImage = ({ name, pictureID, url }) => {
    setImages((prevImages) => ({
      ...prevImages,
      [name]: {
        pictureID,
        url,
      },
    }));
  };

  const removeImage = (name) => {
    setImages((prevImages) => {
      const updatedImages = { ...prevImages };
      delete updatedImages[name]; // Remove the specified image
      return updatedImages;
    });
  };

  const EWTChargesDecision = async ({ewtChargesAccepted, ewtCharges, poaSigner, inspectionID, customerName}) => {
    showLoadingSpinner();
    try {
      const payload = {
        inspectionID,
        ewtChargesAccepted: ewtChargesAccepted,
        ewtCharges,
        poaSigner,
        customerName,
        comment: "No comments"
      };

      const response = await axiosInstance.post("/api/Inspection/EWTChargesDecision", payload);

      const {success} = response.data;

      hideLoadingSpinner();
      return {
        success,
      };
    }
    catch (error) {
      hideLoadingSpinner();
      return {
        success: false,
      };
    }
  };

  const value = {
    inspectionData,
    fetchInspectionData,
    fetchDealerVehicles,
    fetchVehicleDetails,
    addImage,
    removeImage,
    images,
    dealerVehicles,
    submitDealerConditionReport,
    inspection,
    fetchCRMaster,
    submitPOADetails,
    filterVehicles,
    submitDealerOfferDecision,
    setStartDate,
    setEndDate,
    startDate,
    endDate,
    selectedRange,
    setSelectedRange,
    charges,
    error,
    inspectionID,
    EWTChargesDecision
  };

  return (
    <DealerContext.Provider value={value}>
      {children}
    </DealerContext.Provider>
  );
};
